import * as React from 'react';
import { observableValue } from '@vscode/observables';
import { viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import { ConsoleEventsStore, type ConsoleEvent } from '../core/ConsoleEventsStore.js';

class ConsolePanelModel extends ViewModel({
	store: prop.const<ConsoleEventsStore>(),
}) {
	readonly expanded = observableValue(this, false);

	get store(): ConsoleEventsStore { return this.props.store; }

	toggleExpanded(): void {
		this.expanded.set(!this.expanded.get(), undefined);
	}

	clear(): void {
		this.store.clear();
	}
}

export const ConsolePanel = viewWithModel(
	ConsolePanelModel,
	{ store: prop.const<ConsoleEventsStore>() },
	(reader, model) => {
		const store = model.store;
		const events = store.events.read(reader);
		const errorCount = store.errorCount.read(reader);
		const expanded = model.expanded.read(reader);

		if (events.length === 0) {
			return null;
		}

		return (
			<div style={panelStyles.container}>
				<button
					style={panelStyles.header}
					onClick={() => model.toggleExpanded()}
				>
					<span style={panelStyles.headerLeft}>
						<span style={panelStyles.chevron}>{expanded ? '▼' : '▶'}</span>
						<span>Console</span>
						{errorCount > 0 && (
							<span style={panelStyles.errorBadge}>{errorCount}</span>
						)}
						{events.length > errorCount && (
							<span style={panelStyles.warnBadge}>{events.length - errorCount}</span>
						)}
					</span>
					<button
						style={panelStyles.clearButton}
						onClick={(e) => { e.stopPropagation(); model.clear(); }}
						title="Clear console"
					>
						Clear
					</button>
				</button>
				{expanded && (
					<div style={panelStyles.content}>
						{events.map((event: ConsoleEvent) => (
							<ConsoleEventRow key={event.id} event={event} />
						))}
					</div>
				)}
			</div>
		);
	}
);

function ConsoleEventRow({ event }: { event: ConsoleEvent }): React.ReactElement {
	const [showStack, setShowStack] = React.useState(false);
	const isError = event.type === 'console.error' || event.type === 'window.error' || event.type === 'window.unhandledrejection';
	const isWarn = event.type === 'console.warn';

	const time = new Date(event.timestamp);
	const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

	return (
		<div
			style={{
				...rowStyles.container,
				backgroundColor: isError ? 'rgba(255, 80, 80, 0.1)' : isWarn ? 'rgba(255, 200, 0, 0.1)' : 'transparent',
				borderLeft: `3px solid ${isError ? '#f55' : isWarn ? '#fc0' : 'transparent'}`,
			}}
		>
			<span style={rowStyles.time}>{timeStr}</span>
			<span style={{ ...rowStyles.type, color: isError ? '#f77' : isWarn ? '#fc0' : '#888' }}>
				{event.type.replace('window.', '').replace('console.', '')}
			</span>
			<span
				style={{ ...rowStyles.message, cursor: event.stack ? 'pointer' : 'default' }}
				onClick={() => event.stack && setShowStack(!showStack)}
				title={event.stack ? 'Click to toggle stack trace' : undefined}
			>
				{event.message}
				{event.stack && <span style={rowStyles.stackIndicator}> ▾</span>}
			</span>
			{showStack && event.stack && (
				<pre style={rowStyles.stack}>{event.stack}</pre>
			)}
		</div>
	);
}

const panelStyles = {
	container: {
		borderTop: '1px solid var(--component-explorer-panel-border)',
		backgroundColor: 'var(--component-explorer-editor-background)',
		flexShrink: 0,
	} as React.CSSProperties,
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		padding: '6px 12px',
		backgroundColor: 'var(--component-explorer-sideBar-background)',
		border: 'none',
		cursor: 'pointer',
		fontSize: '12px',
		fontWeight: 500,
		color: 'var(--component-explorer-foreground)',
		fontFamily: 'inherit',
	} as React.CSSProperties,
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	} as React.CSSProperties,
	chevron: {
		fontSize: '10px',
		opacity: 0.7,
	} as React.CSSProperties,
	errorBadge: {
		backgroundColor: '#f55',
		color: '#fff',
		fontSize: '10px',
		fontWeight: 600,
		padding: '1px 6px',
		borderRadius: '10px',
		minWidth: '18px',
		textAlign: 'center',
	} as React.CSSProperties,
	warnBadge: {
		backgroundColor: '#a80',
		color: '#fff',
		fontSize: '10px',
		fontWeight: 600,
		padding: '1px 6px',
		borderRadius: '10px',
		minWidth: '18px',
		textAlign: 'center',
	} as React.CSSProperties,
	clearButton: {
		padding: '2px 8px',
		fontSize: '11px',
		backgroundColor: 'transparent',
		border: '1px solid var(--component-explorer-button-border)',
		borderRadius: '3px',
		color: 'var(--component-explorer-foreground)',
		cursor: 'pointer',
		fontFamily: 'inherit',
	} as React.CSSProperties,
	content: {
		maxHeight: '200px',
		overflowY: 'auto',
	} as React.CSSProperties,
};

const rowStyles = {
	container: {
		display: 'grid',
		gridTemplateColumns: 'auto auto 1fr',
		gap: '8px',
		padding: '4px 12px',
		fontSize: '12px',
		fontFamily: 'var(--component-explorer-editor-font-family, monospace)',
		borderBottom: '1px solid var(--component-explorer-panel-border)',
	} as React.CSSProperties,
	time: {
		color: 'var(--component-explorer-descriptionForeground)',
		opacity: 0.7,
	} as React.CSSProperties,
	type: {
		fontWeight: 500,
		minWidth: '80px',
	} as React.CSSProperties,
	message: {
		color: 'var(--component-explorer-foreground)',
		wordBreak: 'break-word',
	} as React.CSSProperties,
	stackIndicator: {
		fontSize: '10px',
		opacity: 0.5,
	} as React.CSSProperties,
	stack: {
		gridColumn: '1 / -1',
		margin: '4px 0 0 0',
		padding: '8px',
		backgroundColor: 'rgba(0, 0, 0, 0.2)',
		borderRadius: '4px',
		fontSize: '11px',
		color: 'var(--component-explorer-descriptionForeground)',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		lineHeight: 1.4,
	} as React.CSSProperties,
};
